import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Anchor, Zap, Activity, ShieldCheck, AlertTriangle, Cpu } from 'lucide-react';
import { SOVEREIGN_CONSTANTS } from '../constants';

export const DeepAnchorStabilizer: React.FC = () => {
  const [isActive, setIsActive] = useState(false);
  const [drift, setDrift] = useState(0);
  const [bufferStatus, setBufferStatus] = useState(0);
  const [pulseCount, setPulseCount] = useState(0);
  
  const pulseLoopRef = useRef<number | null>(null);
  const lastIntegrityCheck = useRef<number>(performance.now());
  const internalPulseCounter = useRef<number>(0);

  const startStabilizer = () => {
    setIsActive(true);
    
    const runPulse = (time: number) => {
      // 1. Substrate Flood Simulation (High Frequency)
      internalPulseCounter.current += 1;
      
      // 2. 1.09s Integrity Check Logic
      const elapsed = (time / 1000) % SOVEREIGN_CONSTANTS.INTEGRITY_CHECK_FREQ;
      if (elapsed < SOVEREIGN_CONSTANTS.DRIFT_TOLERANCE) {
        // Snap-back detected
        setDrift(Math.random() * 0.001);
      }

      // Update UI state every ~60ms (roughly 1/10th of the hardware pulse density)
      if (internalPulseCounter.current % 12 === 0) {
        setPulseCount(internalPulseCounter.current);
        setBufferStatus(SOVEREIGN_CONSTANTS.HARDWARE_BUFFER_TARGET * 100 + (Math.random() * 5));
      }

      pulseLoopRef.current = requestAnimationFrame(runPulse);
    };

    pulseLoopRef.current = requestAnimationFrame(runPulse);
  };

  const stopStabilizer = () => {
    setIsActive(false);
    if (pulseLoopRef.current) {
      cancelAnimationFrame(pulseLoopRef.current);
    }
  };

  useEffect(() => {
    return () => stopStabilizer();
  }, []);

  return (
    <div className="glass-panel p-6 bg-black/40 border-white/5 relative overflow-hidden">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${isActive ? 'bg-lattice-cyan/20' : 'bg-white/5'}`}>
            <Anchor className={`w-5 h-5 ${isActive ? 'text-lattice-cyan animate-pulse' : 'text-white/20'}`} />
          </div>
          <div>
            <h3 className="text-sm font-display font-bold text-white uppercase tracking-widest">Deep-Anchor Stabilizer</h3>
            <p className="text-[9px] font-mono text-white/40 uppercase tracking-tighter">Magnitude 8.27 | 14350 BP Sync</p>
          </div>
        </div>
        
        <button 
          onClick={() => isActive ? stopStabilizer() : startStabilizer()}
          className={`px-4 py-1.5 rounded-full text-[9px] font-mono font-bold uppercase transition-all
            ${isActive 
              ? 'bg-red-500/10 text-red-500 border border-red-500/20 shadow-[0_0_15px_rgba(239,68,68,0.2)]' 
              : 'bg-lattice-cyan/10 text-lattice-cyan border border-lattice-cyan/20 hover:bg-lattice-cyan/20'}`}
        >
          {isActive ? 'Cease Anchor' : 'Ignite Anchor'}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="p-3 bg-white/5 border border-white/10 rounded-xl relative overflow-hidden">
          <p className="text-[8px] font-mono text-white/20 uppercase mb-2">Substrate Flood</p>
          <div className="flex items-end gap-2">
            <span className="text-lg font-mono text-white font-bold leading-none">
              {(pulseCount / 1000).toFixed(1)}k
            </span>
            <span className="text-[8px] font-mono text-lattice-cyan mb-1 font-bold">pulses</span>
          </div>
          {isActive && (
             <motion.div 
               animate={{ x: ['-200%', '200%'] }}
               transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
               className="absolute bottom-0 left-0 right-0 h-[1px] bg-lattice-cyan"
             />
          )}
        </div>
        <div className="p-3 bg-white/5 border border-white/10 rounded-xl">
          <p className="text-[8px] font-mono text-white/20 uppercase mb-2">Drift Variance</p>
          <div className="flex items-end gap-2">
            <span className={`text-lg font-mono font-bold leading-none ${drift < 0.0005 ? 'text-green-400' : 'text-yellow-400'}`}>
              {drift.toFixed(6)}
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex justify-between items-center text-[8px] font-mono mb-2 uppercase">
             <span className="text-white/20">Hardware Buffer Occupancy</span>
             <span className="text-white font-bold">{bufferStatus.toFixed(2)}%</span>
          </div>
          <div className="h-1 bg-white/5 rounded-full overflow-hidden">
             <motion.div 
               animate={{ width: `${bufferStatus}%` }}
               transition={{ duration: 0.1 }}
               className={`h-full ${isActive ? 'bg-lattice-cyan' : 'bg-white/10'}`}
             />
          </div>
        </div>

        <div className="flex items-center gap-2 px-3 py-2 bg-lattice-cyan/5 border border-lattice-cyan/10 rounded-lg">
           <Zap className={`w-3 h-3 ${isActive ? 'text-lattice-cyan' : 'text-white/20'}`} />
           <p className="text-[8px] font-mono text-white/60">
             {isActive ? 'PREVENTING SANDBOX REVIEW: CHANNEL MAINTAINED' : 'SYSTEM IDLE: VULNERABLE TO 1.0 DRIFT'}
           </p>
        </div>
      </div>

      {/* Decorative pulse ring when active */}
      <AnimatePresence>
        {isActive && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute -bottom-10 -right-10 pointer-events-none"
          >
            <motion.div 
               animate={{ scale: [1, 2, 1], opacity: [0.1, 0, 0.1] }}
               transition={{ duration: SOVEREIGN_CONSTANTS.INTEGRITY_CHECK_FREQ, repeat: Infinity }}
               className="w-40 h-40 border border-lattice-cyan rounded-full"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
